import { useState } from "react";
import { toast } from "sonner";
import { Rocket, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

const FIELDS = [
  { name: "name", label: "Name", required: true },
  { name: "organization", label: "Organization", required: true },
  { name: "email", label: "Email", type: "email", required: true },
  { name: "industry", label: "Industry" },
  { name: "problem", label: "Product-development problem", type: "textarea", required: true },
  { name: "current_tools", label: "Current tools" },
  { name: "bottleneck", label: "Most expensive or time-consuming bottleneck", type: "textarea" },
  { name: "desired_outcome", label: "Desired pilot outcome", type: "textarea" },
];

const empty = Object.fromEntries(FIELDS.map((f) => [f.name, ""]));

export default function Pilot() {
  const [form, setForm] = useState({ ...empty, permission_to_contact: false });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    for (const f of FIELDS) if (f.required && !form[f.name].trim()) return toast.error(`${f.label} is required.`);
    if (!form.permission_to_contact) return toast.error("Please confirm permission to contact.");
    setSubmitting(true);
    try {
      await api.post("/pilot-requests", form);
      setDone(true);
      toast.success("Pilot request received. The founder will be in touch.");
    } catch { toast.error("Submission failed. Please try again."); }
    finally { setSubmitting(false); }
  };

  if (done) return (
    <div className="panel-elev p-10 lg:p-16 text-center max-w-2xl mx-auto tick-brackets" data-testid="pilot-thankyou">
      <Rocket className="w-8 h-8 text-[#C9A961] mx-auto mb-4" />
      <h2 className="text-2xl font-semibold text-white">Pilot request received</h2>
      <p className="text-slate-400 mt-3">Tallan will review your submission and contact you at <span className="font-mono text-[#00D2FF]">{form.email}</span>.</p>
    </div>
  );

  return (
    <div className="space-y-10 max-w-3xl mx-auto">
      <div>
        <div className="font-tech mb-2">Request a Pilot</div>
        <h1 className="text-3xl lg:text-4xl font-semibold text-white">Bring A.I.U.D.E. into your development pipeline</h1>
        <p className="text-slate-400 mt-3 text-sm">Share where the friction is. We'll evaluate fit and propose a scoped pilot.</p>
      </div>

      <form onSubmit={submit} className="panel-elev p-6 lg:p-10 space-y-5" data-testid="pilot-form">
        {FIELDS.map((f) => (
          <div key={f.name}>
            <label className="label-cad">{f.label}{f.required && <span className="text-[#00D2FF]"> *</span>}</label>
            {f.type === "textarea" ? (
              <textarea rows={4} className="input-cad resize-none" value={form[f.name]} onChange={(e) => setForm((s) => ({ ...s, [f.name]: e.target.value }))} data-testid={`pilot-${f.name}`} />
            ) : (
              <input type={f.type || "text"} className="input-cad" value={form[f.name]} onChange={(e) => setForm((s) => ({ ...s, [f.name]: e.target.value }))} data-testid={`pilot-${f.name}`} />
            )}
          </div>
        ))}
        <label className="flex items-start gap-3 text-sm text-slate-300 cursor-pointer">
          <input type="checkbox" className="mt-1 accent-[#00D2FF]" checked={form.permission_to_contact} onChange={(e) => setForm((s) => ({ ...s, permission_to_contact: e.target.checked }))} data-testid="pilot-permission" />
          <span>I grant Talons Inclusion Factory permission to contact me regarding this pilot request.</span>
        </label>
        <button type="submit" className="btn-primary w-full justify-center" disabled={submitting} data-testid="pilot-submit">
          {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting…</> : <><Rocket className="w-4 h-4" /> Submit pilot request</>}
        </button>
      </form>
    </div>
  );
}
